import { SettingsPermissions } from "@redotech/account-common/permissions";
import { t } from "@redotech/redo-i18n";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { Navigate, useLocation } from "react-router-dom";
import { SettingsTabRedirect } from "../settings-tab-redirect";
import { fulfillmentSettingsRoutes as originalRoutes } from "./fulfillment";

const orderManagementIndex: PermissionedRouteObject = {
  index: true,
  lazy: async () => {
    const { OrderManagementSettingsPage } =
      await import("../../../../setting/order-management/order-management-settings");
    return {
      Component: function OrderManagementSettingsRoute() {
        const { pathname, search } = useLocation();
        const params = new URLSearchParams(search);
        if (params.get("section") === "order-editing") {
          params.delete("section");
          return (
            <Navigate
              replace
              to={`${pathname}/order-editing${params.size ? `?${params}` : ""}`}
            />
          );
        }
        return <OrderManagementSettingsPage />;
      },
    };
  },
};

function redesignedOmsRoute(
  route: PermissionedRouteObject,
): PermissionedRouteObject {
  if (route.path === "printing" && route.children) {
    return {
      ...route,
      children: [
        {
          index: true,
          element: (
            <SettingsTabRedirect
              from="/settings/oms/printing"
              section="printing"
              to="/settings/order-management"
            />
          ),
        },
        ...route.children.filter((child) => !child.index),
      ],
    };
  }
  const sections: Record<string, string> = {
    general: "order-preparation",
    "automerge-shipments": "merging",
    international: "international",
    printing: "printing",
  };
  const section = route.path && sections[route.path];
  if (section) {
    const { lazy: _lazy, ...base } = route;
    return {
      ...base,
      handle: {
        ...route.handle,
        get breadcrumb() {
          return route.path === "general"
            ? t("Order preparation")
            : route.path === "automerge-shipments"
              ? t("Merge & split rules")
              : route.handle?.breadcrumb;
        },
      },
      element: (
        <SettingsTabRedirect
          from={`/settings/oms/${route.path}`}
          section={section}
          sectionByLegacyTab={
            route.path === "automerge-shipments"
              ? { merging: "merging", splitting: "splitting" }
              : undefined
          }
          to="/settings/order-management"
        />
      ),
    };
  }
  if (
    route.path === "packages" ||
    route.path === "presets" ||
    route.path === "custom-import-schemas"
  ) {
    const { lazy: _lazy, ...base } = route;
    return {
      ...base,
      element: (
        <SettingsTabRedirect
          defaultGroup={
            route.path === "custom-import-schemas"
              ? "fulfillment.custom_imports"
              : route.path === "packages"
                ? "fulfillment.packages"
                : undefined
          }
          from={`/settings/oms/${route.path}`}
          section={
            route.path === "packages"
              ? "order-preparation"
              : "presets-shortcuts"
          }
          to="/settings/order-management"
        />
      ),
    };
  }
  if (route.path === "scanning-warehouse") {
    return {
      ...route,
      lazy: async () => {
        const { ScanningWarehouseSettingsPage } =
          await import("@redotech/redo-merchant-fulfillment-app/setting/scanning-warehouse.new");
        return { Component: ScanningWarehouseSettingsPage };
      },
    };
  }
  if (route.path === "security") {
    const { lazy: _lazy, ...base } = route;
    return {
      ...base,
      element: (
        <SettingsTabRedirect
          from="/settings/oms/security"
          section="access-devices"
          to="/settings/order-management"
        />
      ),
    };
  }
  return route;
}

export const fulfillmentSettingsRoutes: PermissionedRouteObject[] =
  originalRoutes.map((route) => {
    if (route.index) {
      return route;
    }
    if (route.path === "order-management") {
      return {
        ...route,
        handle: {
          ...route.handle,
          permission: [
            SettingsPermissions.SET_GENERAL_SETTINGS,
            SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
            SettingsPermissions.SET_SHIPPING_SETTINGS,
          ],
        },
        children: [orderManagementIndex, ...(route.children ?? [])],
      };
    }
    return route.path === "oms"
      ? {
          ...route,
          handle: {
            ...route.handle,
            get breadcrumb() {
              return t("Order fulfillment");
            },
          },
          children: route.children?.map(redesignedOmsRoute),
        }
      : route;
  });
